// Phase 2 "fast media": a card's picture (the render's poster) that plays its silent ~480p preview while a mouse hovers
// the card, or while a finger holds it (holdpreview.ts). The preview's <video> only exists while it plays, so a page of
// cards loads one small JPEG each and no video at all. Holding a card to peek doesn't open it; a tap still does.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { HoldGesture } from "../holdpreview";

export function PosterPreview({ poster, preview, blank, className = "" }: { poster?: string | null; preview?: string | null; blank?: ReactNode; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [live, setLive] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    setLive(false);
    const el = ref.current;
    if (!el || !preview) return;
    // the card (a link or a button) is what's hovered and held
    const host = (el.closest("a,button") as HTMLElement | null) ?? el;
    const g = new HoldGesture(setLive);
    const touch = (e: PointerEvent) => e.pointerType !== "mouse";
    const enter = (e: PointerEvent) => !touch(e) && setLive(true);
    const leave = (e: PointerEvent) => (touch(e) ? g.cancel() : setLive(false));
    const down = (e: PointerEvent) => touch(e) && g.down(e.clientX, e.clientY);
    const move = (e: PointerEvent) => touch(e) && g.move(e.clientX, e.clientY);
    const up = (e: PointerEvent) => touch(e) && g.up();
    const cancel = () => g.cancel();
    const click = (e: MouseEvent) => {
      if (!g.takeClick()) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const menu = (e: Event) => g.held && e.preventDefault(); // Android's long-press menu
    const opts = { passive: true } as const;
    host.addEventListener("pointerenter", enter, opts);
    host.addEventListener("pointerleave", leave, opts);
    host.addEventListener("pointerdown", down, opts);
    host.addEventListener("pointermove", move, opts);
    host.addEventListener("pointerup", up, opts);
    host.addEventListener("pointercancel", cancel, opts);
    host.addEventListener("click", click, true);
    host.addEventListener("contextmenu", menu);
    host.classList.add("pp-host");
    return () => {
      g.cancel();
      host.removeEventListener("pointerenter", enter);
      host.removeEventListener("pointerleave", leave);
      host.removeEventListener("pointerdown", down);
      host.removeEventListener("pointermove", move);
      host.removeEventListener("pointerup", up);
      host.removeEventListener("pointercancel", cancel);
      host.removeEventListener("click", click, true);
      host.removeEventListener("contextmenu", menu);
      host.classList.remove("pp-host");
    };
  }, [preview]);

  useEffect(() => {
    if (!live) setPlaying(false);
  }, [live]);

  return (
    <span ref={ref} className={`pp${playing ? " is-playing" : ""} ${className}`}>
      {poster ? <img src={poster} alt="" loading="lazy" decoding="async" draggable={false} /> : (blank ?? <span className="pp-blank" />)}
      {live && preview && <video src={preview} poster={poster ?? undefined} muted loop autoPlay playsInline preload="auto" aria-hidden tabIndex={-1} onPlaying={() => setPlaying(true)} />}
    </span>
  );
}
